/**
 * Authentication Context Provider
 * Manages user authentication state and provides auth methods throughout the app
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';

interface UserInfo {
  userId: string;
  email: string;
  displayName: string;
  tenantId?: string;
  role?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserInfo | null;
  accessToken: string | null;
  login: () => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      if (inProgress === InteractionStatus.None && isAuthenticated && accounts.length > 0) {
        try {
          // Get access token
          const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });

          setAccessToken(response.accessToken);

          // Extract user info from token claims
          const idTokenClaims = response.idTokenClaims as any;
          const userInfo: UserInfo = {
            userId: idTokenClaims.sub || idTokenClaims.oid || accounts[0].localAccountId,
            email: idTokenClaims.emails?.[0] || accounts[0].username,
            displayName: idTokenClaims.name || accounts[0].name || 'User',
          };

          setUser(userInfo);
          setError(null);
        } catch (err: any) {
          console.error('Failed to acquire token:', err);

          // If authority mismatch, clear all accounts and force re-login
          if (err.errorCode === 'authority_mismatch' || err.message?.includes('authority_mismatch')) {
            console.warn('Authority mismatch detected - clearing cached accounts');
            // Clear all cached accounts
            const allAccounts = instance.getAllAccounts();
            for (const account of allAccounts) {
              await instance.clearCache({ account });
            }
            // Force logout to clear session
            instance.logoutRedirect({
              postLogoutRedirectUri: window.location.origin,
            });
            return;
          }

          setError(err.message);
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, [isAuthenticated, accounts, instance, inProgress]);

  const login = async () => {
    try {
      setError(null);

      // Clear any cached accounts from old tenants before login
      const allAccounts = instance.getAllAccounts();
      if (allAccounts.length > 0) {
        console.log('Clearing cached accounts before login:', allAccounts.length);
        for (const account of allAccounts) {
          await instance.clearCache({ account });
        }
      }

      // Use redirect instead of popup to avoid COOP warnings
      await instance.loginRedirect(loginRequest);
    } catch (err: any) {
      console.error('Login failed:', err);
      setError(err.message);
    }
  };

  const logout = () => {
    // Use redirect instead of popup to avoid COOP warnings
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
    setUser(null);
    setAccessToken(null);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        accessToken,
        login,
        logout,
        isLoading,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
